import React, { useState, useEffect } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import FormLabel from '@material-ui/core/FormLabel'
import FormControl from '@material-ui/core/FormControl'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import AdminAPI from 'config/api/AdminAPI'
import { NotificationManager } from 'react-notifications'
import { onError } from 'config/lib/errorLib'
import Loader from 'components/general/Loader'

// Format a Date as YYYY-MM-DD (local).
function toYMD(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

function todayYMD() {
    return toYMD(new Date())
}

// The next Monday strictly after the given YYYY-MM-DD date.
function nextMondayAfter(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr + 'T00:00:00')
    if (isNaN(d.getTime())) return ''
    const day = d.getDay() // 0 Sun .. 1 Mon .. 6 Sat
    let diff = (1 - day + 7) % 7
    if (diff === 0) diff = 7 // if it's a Monday, use the following Monday
    d.setDate(d.getDate() + diff)
    return toYMD(d)
}

function SendCardLinkModal({ open, onClose, companyDetails }) {
    const api = new AdminAPI()

    const planDetails = companyDetails.planDetails || {}
    const defaultStart = todayYMD()
    const defaultMaxCap = planDetails.maxLeads ?? ''
    const defaultPrice = planDetails.leadPrice ?? ''
    const defaultEmail = companyDetails.billingEmail
        || (companyDetails.primaryPerson && companyDetails.primaryPerson.email)
        || ''

    const [email, setEmail] = useState(defaultEmail)
    const [leadStartDate, setLeadStartDate] = useState(defaultStart)
    const [firstBillingDate, setFirstBillingDate] = useState(nextMondayAfter(defaultStart))
    const [maxCap, setMaxCap] = useState(defaultMaxCap)
    const [pricePerLead, setPricePerLead] = useState(defaultPrice)
    const [loading, setLoading] = useState(false)
    const [link, setLink] = useState(null)
    const [emailedTo, setEmailedTo] = useState(null)
    const [existingLink, setExistingLink] = useState(null) // previously generated, still-active link

    // On open, look up whether an active (unused, unexpired) link already exists.
    useEffect(() => {
        if (!open || !companyDetails.companyId) return
        let cancelled = false
        ;(async () => {
            try {
                const result = await api.getCardLink(companyDetails.companyId)
                if (!cancelled) setExistingLink(result && result.exists ? result : null)
            } catch (e) {
                onError(e)
            }
        })()
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, companyDetails.companyId])

    // When the lead start date changes, move first billing to the next Monday after it.
    const handleStartChange = (value) => {
        setLeadStartDate(value)
        setFirstBillingDate(nextMondayAfter(value))
    }

    const createLink = async () => {
        setLoading(true)
        try {
            const result = await api.createCardLink(
                companyDetails.companyId,
                leadStartDate,
                firstBillingDate,
                maxCap,
                email,
                pricePerLead
            )
            if (result && result.success) {
                setLink(result.link)
                setEmailedTo(result.emailedTo)
                NotificationManager.success(
                    result.emailedTo ? ('Link created and emailed to ' + result.emailedTo) : 'Link created'
                )
            } else {
                NotificationManager.warning((result && result.message) || 'Could not create link')
            }
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    const copyUrl = (url) => {
        if (url && navigator.clipboard) {
            navigator.clipboard.writeText(url)
            NotificationManager.success('Link copied to clipboard')
        }
    }

    const copyLink = () => copyUrl(link)

    // Format an epoch (ms) as e.g. "9 Jul 2026".
    const formatDisplayDate = (ms) => {
        if (!ms) return ''
        return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }

    const handleClose = () => {
        setLink(null)
        setEmailedTo(null)
        onClose()
    }

    const labelStyle = {
        color: '#8898aa',
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginBottom: '0.4rem',
        display: 'block'
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogContent style={{ padding: '2rem' }}>
                <Box mb={3}>
                    <Typography variant='h3' style={{ color: '#32325d', marginBottom: '0.5rem' }}>
                        Send Add-Card Link
                    </Typography>
                    <Typography variant='body2' style={{ color: '#8898aa' }}>
                        {companyDetails.name} can add a payment card without logging in. The link is
                        single-use and expires after 7 days.
                    </Typography>
                </Box>

                {!link ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormLabel style={labelStyle}>Recipient Email</FormLabel>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput
                                    type='email'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder='billing@company.com'
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel style={labelStyle}>Leads Start Date</FormLabel>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput
                                    type='date'
                                    value={leadStartDate}
                                    onChange={(e) => handleStartChange(e.target.value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel style={labelStyle}>First Billing Date</FormLabel>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput
                                    type='date'
                                    value={firstBillingDate}
                                    onChange={(e) => setFirstBillingDate(e.target.value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel style={labelStyle}>Weekly Lead Cap</FormLabel>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput
                                    type='number'
                                    value={maxCap}
                                    onChange={(e) => setMaxCap(e.target.value)}
                                    placeholder='e.g. 20'
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel style={labelStyle}>Price Per Lead (€)</FormLabel>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput
                                    type='number'
                                    value={pricePerLead}
                                    onChange={(e) => setPricePerLead(e.target.value)}
                                    placeholder='e.g. 50'
                                />
                            </FormControl>
                        </Grid>
                        {existingLink && (
                            <Grid item xs={12}>
                                <Box style={{ border: '1px solid #cfe9d2', background: '#f3faf4', borderRadius: 8, padding: '0.75rem 1rem' }}>
                                    <Typography variant='body2' style={{ color: '#1f3a29', fontWeight: 600 }}>
                                        An active link already exists
                                    </Typography>
                                    <Typography variant='caption' style={{ color: '#6b7c93', display: 'block', marginBottom: '0.5rem' }}>
                                        Created {formatDisplayDate(existingLink.createdAt)} · Expires {formatDisplayDate(existingLink.expiresAt * 1000)}
                                    </Typography>
                                    <Box display='flex' alignItems='center'>
                                        <FormControl variant='filled' fullWidth style={{ marginRight: '0.5rem' }}>
                                            <OutlinedInput value={existingLink.link} readOnly style={{ fontSize: '0.8125rem' }} />
                                        </FormControl>
                                        <Button
                                            variant='outlined'
                                            color='primary'
                                            onClick={() => copyUrl(existingLink.link)}
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            Copy
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' mt={2}>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={handleClose}
                                    style={{ marginRight: '1rem' }}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={createLink}
                                    disabled={loading || !email || !leadStartDate || !firstBillingDate || maxCap === '' || pricePerLead === ''}
                                >
                                    {loading ? <Loader height={12} /> : 'Create & Send Link'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant='body2' style={{ color: '#525f7f' }}>
                                {emailedTo
                                    ? ('Link created and emailed to ' + emailedTo + '. You can also copy it below.')
                                    : 'Link created. No billing email on file. Copy and send it manually below.'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl variant='filled' fullWidth>
                                <OutlinedInput value={link} readOnly />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' mt={2}>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={copyLink}
                                    style={{ marginRight: '1rem' }}
                                >
                                    Copy Link
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={handleClose}
                                >
                                    Done
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default SendCardLinkModal
